export const DATASTREAM_MIN_MAX_ABS_LIMIT = 1_000_000_000;

const NUMERIC_DATASTREAM_TYPES = new Set(["integer", "int", "double", "float", "number"]);
const REPORT_DOWNLOAD_DATASTREAM_TYPES = new Set([
  ...NUMERIC_DATASTREAM_TYPES,
  "string",
  "text",
]);

const hasValue = (value) => value !== "" && value !== null && value !== undefined;

const toFiniteNumberOrNull = (value) => {
  if (!hasValue(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatLimit = () => DATASTREAM_MIN_MAX_ABS_LIMIT.toLocaleString();
const normalizeDatastreamTitleKey = (value) => String(value ?? "").trim().toLowerCase();
const getDatastreamLabel = (datastream, index) => {
  const name = String(datastream?.name ?? "").trim();
  const pin = String(datastream?.pin ?? "").trim();

  if (name && pin && name !== pin) {
    return `${name} (${pin})`;
  }

  return name || pin || `#${index + 1}`;
};
const formatMessageTemplate = (template, fallback, replacements = {}) => {
  const source = typeof template === "string" && template.trim() ? template : fallback;

  return source.replace(/\{(\w+)\}/g, (match, key) => {
    if (!Object.prototype.hasOwnProperty.call(replacements, key)) {
      return match;
    }

    const value = replacements[key];
    return value === null || value === undefined ? "" : String(value);
  });
};

export const normalizeDatastreamType = (value) => String(value ?? "").trim().toLowerCase();

export const isNumericDatastreamType = (value) =>
  NUMERIC_DATASTREAM_TYPES.has(normalizeDatastreamType(value));

export const normalizeDatastreamBooleanFlag = (value, defaultValue = false) => {
  if (value === undefined || value === null || value === "") {
    return defaultValue;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0) return false;
    return defaultValue;
  }

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "on"].includes(normalized)) return true;
    if (["false", "0", "no", "off"].includes(normalized)) return false;
  }

  return defaultValue;
};

export const isDatastreamHistoryEnabled = (datastream) =>
  normalizeDatastreamBooleanFlag(datastream?.enableHistory, true);

export const isDatastreamReportsEnabled = (datastream) =>
  normalizeDatastreamBooleanFlag(datastream?.reports, true);

export const normalizeReportPriority = (value, fallback = null) => {
  const parsed = Number(value);
  if (Number.isInteger(parsed) && parsed > 0) return parsed;

  const parsedFallback = Number(fallback);
  return Number.isInteger(parsedFallback) && parsedFallback > 0 ? parsedFallback : null;
};

export const sortDatastreamsByReportPriority = (datastreams = []) =>
  (Array.isArray(datastreams) ? datastreams : [])
    .map((datastream, index) => ({ datastream, index }))
    .sort((left, right) => {
      const leftPriority = normalizeReportPriority(left.datastream?.reportPriority, left.index + 1);
      const rightPriority = normalizeReportPriority(right.datastream?.reportPriority, right.index + 1);
      return leftPriority - rightPriority || left.index - right.index;
    })
    .map(({ datastream }) => datastream);

export const isReportDownloadDatastream = (datastream) => {
  if (!datastream?.pin) return false;
  if (!isDatastreamHistoryEnabled(datastream)) return false;
  if (!isDatastreamReportsEnabled(datastream)) return false;

  const dataType = datastream?.dataType ?? datastream?.datatype ?? datastream?.data_type;
  return REPORT_DOWNLOAD_DATASTREAM_TYPES.has(normalizeDatastreamType(dataType));
};

export const normalizeTemplateDatastream = (datastream) => {
  if (!datastream || typeof datastream !== "object") {
    return datastream;
  }

  return {
    ...datastream,
    isControlled: normalizeDatastreamBooleanFlag(datastream.isControlled, false),
  };
};

export const normalizeTemplateDatastreamCollection = (datastreams = []) =>
  (Array.isArray(datastreams) ? datastreams : [])
    .filter(Boolean)
    .map((datastream, index) => ({
      ...normalizeTemplateDatastream(datastream),
      reportPriority: normalizeReportPriority(datastream?.reportPriority, index + 1),
    }));

export const validateDatastreamMinMax = (datastream, messages = {}) => {
  const dataType = datastream?.dataType ?? datastream?.datatype ?? datastream?.data_type;
  const isNumeric = isNumericDatastreamType(dataType);

  if (!isNumeric) {
    return {
      valid: true,
      normalized: {
        min: null,
        max: null,
        defaultValue: datastream?.defaultValue ?? null,
      },
    };
  }

  const minNum = toFiniteNumberOrNull(datastream?.min);
  const maxNum = toFiniteNumberOrNull(datastream?.max);

  if (minNum === null || maxNum === null) {
    return {
      valid: false,
      field: "range",
      message: messages.minMaxRequired || "Min and Max are required and must be valid numbers.",
    };
  }

  if (
    Math.abs(minNum) > DATASTREAM_MIN_MAX_ABS_LIMIT ||
    Math.abs(maxNum) > DATASTREAM_MIN_MAX_ABS_LIMIT
  ) {
    return {
      valid: false,
      field: "range",
      message:
        messages.outOfBounds ||
        `Min and Max must be between -${formatLimit()} and ${formatLimit()}.`,
    };
  }

  if (minNum >= maxNum) {
    return {
      valid: false,
      field: "range",
      message: messages.minLessThanMax || "Min must be less than Max.",
    };
  }

  const defaultRaw = datastream?.defaultValue;
  if (hasValue(defaultRaw)) {
    const defaultNum = Number(defaultRaw);
    if (!Number.isFinite(defaultNum)) {
      return {
        valid: false,
        field: "defaultValue",
        message: messages.defaultInvalid || "Default value must be a valid number.",
      };
    }

    if (defaultNum < minNum || defaultNum > maxNum) {
      return {
        valid: false,
        field: "defaultValue",
        message: messages.defaultOutOfRange || "Default value must be between Min and Max.",
      };
    }

    return {
      valid: true,
      normalized: {
        min: minNum,
        max: maxNum,
        defaultValue: defaultNum,
      },
    };
  }

  return {
    valid: true,
    normalized: {
      min: minNum,
      max: maxNum,
      defaultValue: null,
    },
  };
};

export const validateDatastreamCollection = (datastreams = [], messages = {}) => {
  if (!Array.isArray(datastreams)) {
    return {
      valid: false,
      message: formatMessageTemplate(
        messages.invalidCollection,
        "Datastreams payload is invalid."
      ),
    };
  }

  const seenTitles = new Map();

  for (let index = 0; index < datastreams.length; index += 1) {
    const datastream = datastreams[index];

    if (!datastream || typeof datastream !== "object") {
      return {
        valid: false,
        index,
        message: formatMessageTemplate(
          messages.invalidDatastream,
          "Datastream at row {row} is invalid.",
          {
            row: index + 1,
          }
        ),
      };
    }

    const result = validateDatastreamMinMax(datastream, messages);
    if (!result.valid) {
      const label = getDatastreamLabel(datastream, index);
      return {
        valid: false,
        index,
        field: result.field,
        message: `${messages.datastreamPrefix || "Datastream"} "${label}": ${result.message}`,
      };
    }

    const normalizedTitle = normalizeDatastreamTitleKey(datastream?.name);
    if (!normalizedTitle) {
      continue;
    }

    if (seenTitles.has(normalizedTitle)) {
      const conflictingIndex = seenTitles.get(normalizedTitle);
      const conflictingDatastream = datastreams[conflictingIndex];
      const duplicateTitle = String(datastream.name).trim();
      const conflictingLabel = getDatastreamLabel(conflictingDatastream, conflictingIndex);
      const targetLabel = getDatastreamLabel(datastream, index);
      return {
        valid: false,
        index,
        field: "name",
        message: formatMessageTemplate(
          messages.duplicateTitle,
          'Datastream title "{title}" is already used by datastream "{conflict}". Please choose another title for datastream "{target}".',
          {
            title: duplicateTitle,
            conflict: conflictingLabel,
            target: targetLabel,
          }
        ),
      };
    }

    seenTitles.set(normalizedTitle, index);
  }

  return { valid: true };
};
